import type { FocusEventHandler, TextareaHTMLAttributes } from 'react';

import { cn } from '../../utils/cn';
import type { TextInputChangeEvent, TextInputProps } from './TextInput.types';

/**
 * Web TextInput — renders a native `<input>` (or `<textarea>` when
 * `multiline`) with UA styling stripped so the same className produces the same
 * visual output as react-native's TextInput on mobile. Font/color inherit from
 * the styled wrapper (mirrors the Text primitive's reset).
 */
const RESET_CLASSES =
  'm-0 min-w-0 border-0 bg-transparent p-0 font-inherit text-inherit outline-none';

export function TextInput({
  className,
  value,
  defaultValue,
  placeholder,
  placeholderTextColor: _placeholderTextColor,
  onChangeText,
  onFocus,
  onBlur,
  disabled,
  multiline,
  ref,
  accessibilityLabel,
  testID,
  nativeInputComponent: _nativeInputComponent,
  webProps,
  nativeProps: _nativeProps,
}: TextInputProps) {
  const shared = {
    className: cn(RESET_CLASSES, multiline && 'resize-none', className),
    value,
    defaultValue,
    placeholder,
    disabled,
    'aria-label': accessibilityLabel ?? webProps?.['aria-label'],
    'data-testid': testID,
  };

  if (multiline) {
    // `<input>` can't be multiline, so render a `<textarea>`. `webProps` is
    // typed for `<input>`, but the attributes callers actually pass (id, name,
    // aria-*, autoComplete, style, onPointerDown, …) are valid on both — cast
    // rather than fork the prop type. Callers never pass input-only attrs
    // (`type`, `checked`) to the Textarea component.
    return (
      <textarea
        {...(webProps as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        {...shared}
        // `as never`: the ref forwards fine at runtime; the cast dodges the
        // dual-@types/react nominal mismatch when the consuming app builds this
        // shared file through project references.
        ref={ref as never}
        onChange={(event) =>
          onChangeText?.(event.target.value, event as unknown as TextInputChangeEvent)
        }
        onFocus={onFocus as unknown as FocusEventHandler<HTMLTextAreaElement>}
        onBlur={onBlur as unknown as FocusEventHandler<HTMLTextAreaElement>}
      />
    );
  }

  return (
    <input
      {...webProps}
      {...shared}
      ref={ref as never}
      onChange={(event) => onChangeText?.(event.target.value, event)}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
}
